import React, { FC, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { getNotice } from '@src/redux/selectors/notice';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import {
  MessageThreadStateEnum,
  useAcceptApplicationMutation,
  useGetInboxMessagedOpeningsLazyQuery,
  useGetVerifiedUserForChatPageLazyQuery,
  useMarkInboxMessageThreadAsReadMutation,
  useMarkInboxMessageThreadAsUnreadMutation,
  useRejectApplicationMutation,
  useSendInboxMessageMutation,
  useSessionQuery,
  useTeamMembersQueryQuery,
  useUpdateInboxMessageThreadMutation,
} from '@src/graphql/generated';
import { DEFAULT_CURRENCY_CODE } from '@src/constants';
import { useAppDispatch, useAppSelector } from '@src/redux';
import { TalentCard } from '@src/containers/Source/TalentCard';
import { closeModal, setModalContent } from '@src/redux/actions/modal';
import { ACTIONS_MODAL_CLASS_NAME, CandidateProfileActionsModal } from '@src/containers/CandidateProfileActionsModal';
import { trackEvent } from '@src/metrics';
import { ApolloErrorCode } from '@src/enums/apolloErrorCode.enum';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { useChatMessage } from '@src/customHooks/useChatMessage';
import { Tabs } from './enums/tabs.enum';
import { ContentHeader } from './ContentHeader';
import { QueryParams } from './interfaces/queryParams.interface';
import { FIRST_BATCH_LIMIT } from './constants/filters';
import { useMessageThreads } from './hooks/useMessageThreads';
import { Sidebar } from './Sidebar';
import { Conversation } from './Conversation';
import { useMessageThread } from './hooks/useMessageThread';
import { SelectedCandidatesFilter } from './enums/selectedCandidatesFilter.enum';
import './styles.scss';

const RECRUITER_STATE_TO_SUCCESS_MESSAGE: Record<MessageThreadStateEnum, string> = {
  [MessageThreadStateEnum.OPEN]: 'Chat reopened',
  [MessageThreadStateEnum.CLOSED]: 'Chat closed',
  [MessageThreadStateEnum.ARCHIVED]: 'Chat archived',
};

export const Inbox: FC = () => {
  const { queryParams, updateQueryParams, removeQueryParams } = useQueryParams<QueryParams>();

  const dispatch = useAppDispatch();

  const noticeData = useAppSelector(getNotice);

  const notice = noticeData?.notice;

  const { chatMessage, setChatMessage, clearChatMessage } = useChatMessage({
    chatId: queryParams.threadId,
  });

  const { data: teamMembersData } = useTeamMembersQueryQuery();

  const teamMembers = teamMembersData?.company.teamMembers || [];

  const { data: sessionData } = useSessionQuery();

  const recruiter = sessionData?.currentRecruiter;
  const hasInboxAccess = !!recruiter?.rights.openings;

  const {
    isMessageThreadsLoading,
    messageThreads,
    messageThreadsPagination,
    isMessageThreadsInitialLoading,
    loadMoreMessageThreads,
    updateMessageThreadsQuery,
  } = useMessageThreads({ currentRecruiterId: recruiter?.id });

  const { messageThreadData, refetchMessageThread, isMessageThreadLoading } = useMessageThread();

  const applicationId = messageThreadData?.messageThread.application?.id;
  const openingId = messageThreadData?.messageThread.opening?.id;
  const userId = messageThreadData?.messageThread.user?.id;
  const userMatchingProfileId = messageThreadData?.messageThread.application?.userMatchingProfile?.id;

  const [fetchMessagedOpenings, { data: openingsData, refetch: refetchMessagedOpenings }] =
    useGetInboxMessagedOpeningsLazyQuery();

  const openings = openingsData?.messagedOpenings.result || [];

  const [fetchVerifiedUserForChatPage, { data: getVerifiedUserForChatPageData }] =
    useGetVerifiedUserForChatPageLazyQuery({
      fetchPolicy: 'network-only',
      onError: error => {
        if (error.graphQLErrors.length) {
          error.graphQLErrors.forEach(graphQLError => {
            if (
              (graphQLError?.extensions?.exception?.status && graphQLError?.extensions?.exception?.status !== 404) ||
              (graphQLError?.extensions?.code && graphQLError?.extensions?.code !== ApolloErrorCode.RESOURCE_NOT_FOUND)
            ) {
              dispatch(setErrorFlashMessage());
            }
          });
        }
      },
    });

  const userVerificationProfile = getVerifiedUserForChatPageData?.verifiedUser;

  const [markMessageThreadAsRead] = useMarkInboxMessageThreadAsReadMutation({
    onError: () => {
      dispatch(setErrorFlashMessage('Error marking chat as read'));
    },
  });

  const markRead = async (threadId: string) => {
    await markMessageThreadAsRead({
      variables: {
        messageThreadId: threadId,
      },
    });
  };

  const selectChat = useCallback(
    (nextChatId: string): void => {
      if (!queryParams.threadId || nextChatId !== queryParams.threadId) {
        updateQueryParams({
          activeTab: Tabs.CONVERSATION,
          threadId: nextChatId,
        });
        markRead(nextChatId);
      }
    },
    [queryParams],
  );

  const [markMessageThreadAsUnread] = useMarkInboxMessageThreadAsUnreadMutation({
    onCompleted: async () => {
      dispatch(setSuccessFlashMessage('Chat marked as unread'));
    },
    onError: () => {
      dispatch(setErrorFlashMessage('Error marking chat as unread'));
    },
  });

  const [acceptApplication] = useAcceptApplicationMutation({
    onCompleted: async () => {
      dispatch(setSuccessFlashMessage('Application accepted'));

      await refetchMessageThread();

      clearChatMessage();
    },
    onError: () => {
      dispatch(setErrorFlashMessage('Error accepting application'));
    },
  });

  const [rejectApplication] = useRejectApplicationMutation({
    onCompleted: async () => {
      dispatch(setSuccessFlashMessage('Application rejected'));

      updateMessageThreadsQuery(data => {
        return {
          ...data,
          messageThreads: {
            ...data.messageThreads,
            result: data.messageThreads.result.filter(({ id }) => id !== queryParams.threadId),
          },
        };
      });

      clearChatMessage();

      removeQueryParams(['threadId']);

      if (
        messageThreads.length < FIRST_BATCH_LIMIT &&
        messageThreadsPagination?.hasMoreResults &&
        !isMessageThreadsLoading
      ) {
        loadMoreMessageThreads();
      }
    },
    onError: () => {
      dispatch(setErrorFlashMessage('Error rejecting application'));
    },
  });

  const [updateInboxMessageThread] = useUpdateInboxMessageThreadMutation({
    onCompleted: async updateInboxMessageThreadData => {
      const updatedRecruiterState = updateInboxMessageThreadData.updateMessageThread.recruiterState;

      dispatch(setSuccessFlashMessage(RECRUITER_STATE_TO_SUCCESS_MESSAGE[updatedRecruiterState]));

      if ([MessageThreadStateEnum.ARCHIVED, MessageThreadStateEnum.CLOSED].includes(updatedRecruiterState)) {
        removeQueryParams(['threadId']);

        return;
      }

      if (MessageThreadStateEnum.OPEN === updatedRecruiterState) {
        await refetchMessageThread();
      }
    },
    onError: () => {
      dispatch(setErrorFlashMessage());
    },
  });

  const [sendInboxMessage] = useSendInboxMessageMutation({
    onCompleted: async () => {
      clearChatMessage();
    },
    onError: () => {
      dispatch(setErrorFlashMessage('Error sending message'));
    },
  });

  const onInputChange = (chatInputMessage: string): void => {
    setChatMessage(chatInputMessage);
  };

  const onTabSwitch = (activeTab: Tabs): void => {
    updateQueryParams({ activeTab: activeTab });
  };

  const closeThread = async (): Promise<void> => {
    if (!messageThreadData?.messageThread.id) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await updateInboxMessageThread({
      variables: {
        messageThreadId: messageThreadData.messageThread.id,
        updateInput: {
          state: MessageThreadStateEnum.CLOSED,
        },
      },
    });
  };

  const archiveThread = async (): Promise<void> => {
    if (!messageThreadData?.messageThread.id) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await updateInboxMessageThread({
      variables: {
        messageThreadId: messageThreadData.messageThread.id,
        updateInput: {
          state: MessageThreadStateEnum.ARCHIVED,
        },
      },
    });
  };

  const sendMessageToThread = async (message: string): Promise<void> => {
    if (!queryParams.threadId || !message.length) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await sendInboxMessage({
      variables: {
        messageInput: {
          message: message,
        },
        messageThreadId: queryParams.threadId,
      },
    });
  };

  const markUnread = async () => {
    if (!queryParams.threadId) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await markMessageThreadAsUnread({
      variables: {
        messageThreadId: queryParams.threadId,
      },
    });
  };

  const onAccept = async (): Promise<void> => {
    if (!userMatchingProfileId || !openingId) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await acceptApplication({
      variables: {
        message: chatMessage,
        openingId: openingId,
        userMatchingProfileId: userMatchingProfileId,
      },
    });
  };

  const onDecline = async (): Promise<void> => {
    if (!userMatchingProfileId || !openingId) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await rejectApplication({
      variables: {
        message: chatMessage,
        openingId: openingId,
        userMatchingProfileId: userMatchingProfileId,
      },
    });
  };

  const onReopen = async (): Promise<void> => {
    if (!messageThreadData?.messageThread.id) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await updateInboxMessageThread({
      variables: {
        messageThreadId: messageThreadData.messageThread.id,
        updateInput: {
          state: MessageThreadStateEnum.OPEN,
        },
      },
    });
  };

  const openedChat = messageThreads.find(chat => chat.id === queryParams.threadId);

  const openActionsModal = () => {
    dispatch(
      setModalContent({
        className: ACTIONS_MODAL_CLASS_NAME,
        content: (
          <CandidateProfileActionsModal
            applicationId={applicationId}
            closeModal={() => dispatch(closeModal())}
            defaultInputMessage={chatMessage}
            onChatClosed={() => {
              if (openedChat?.id) {
                closeThread();
              }
            }}
            openingId={openingId}
            sendMessage={async text => {
              await sendMessageToThread(text);
            }}
            threadId={queryParams.threadId}
          />
        ),
      }),
    );

    trackEvent({
      context: MetricContext.END_CHAT,
      interaction: MetricAction.OPEN_END_CHAT_VIEW,
      payload: {
        'Application Id': applicationId,
        'Event Source': MetricEventSource.CANDIDATE_PROFILE_CLOSE_CHAT_ACTION,
        'Opening Id': openingId,
        'Previous Context': MetricContext.CHATS,
        'Thread Id': queryParams.threadId,
        'Type': 'Close',
      },
    });
  };

  useEffect(() => {
    if (!queryParams.selectedRecruiter) {
      return;
    }

    if (!openingsData) {
      fetchMessagedOpenings({
        variables: {
          recruiterId: queryParams.selectedRecruiter,
        },
      });

      return;
    }

    refetchMessagedOpenings({
      recruiterId: queryParams.selectedRecruiter,
    });
  }, [queryParams.selectedRecruiter]);

  useEffect(() => {
    if (userId) {
      fetchVerifiedUserForChatPage({
        variables: {
          recruiterCurrencyCode: recruiter?.country?.currency || DEFAULT_CURRENCY_CODE,
          userId: userId,
        },
      });
    }
  }, [userId]);

  useEffect(() => {
    if (!queryParams.selectedCandidatesFilter && !queryParams.selectedRecruiter) {
      updateQueryParams({
        activeTab: Tabs.CONVERSATION,
        selectedCandidatesFilter: SelectedCandidatesFilter.ALL,
        selectedRecruiter: recruiter?.id,
      });
    }
  }, [queryParams]);

  return (
    <div className={classnames('inbox', !!notice && 'inbox--notice')}>
      <div className="inbox__sidebar">
        <Sidebar
          hasInboxAccess={!!hasInboxAccess}
          isMessageThreadsInitialLoading={isMessageThreadsInitialLoading}
          isMessageThreadsLoading={isMessageThreadsLoading}
          loadMoreMessageThreads={loadMoreMessageThreads}
          messageThreads={messageThreads}
          messageThreadsPagination={messageThreadsPagination}
          openedChat={openedChat}
          openingId={openingId}
          openings={openings}
          selectChat={selectChat}
          teamMembers={teamMembers}
          threadId={queryParams.threadId}
        />
      </div>

      <div className="inbox__content">
        {!!userVerificationProfile && !!queryParams.threadId && (
          <ContentHeader activeTab={queryParams.activeTab} onTabSwitch={onTabSwitch} />
        )}

        {(!userVerificationProfile || queryParams.activeTab === Tabs.CONVERSATION) && (
          <Conversation
            chatInputMessage={chatMessage}
            doesUserVerificationProfileExist={!!userVerificationProfile}
            isThreadLoading={isMessageThreadLoading}
            markUnread={markUnread}
            messageThread={messageThreadData?.messageThread}
            onAccept={onAccept}
            onChatArchive={archiveThread}
            onClose={closeThread}
            onDecline={onDecline}
            onInputChange={onInputChange}
            onReopen={onReopen}
            openActionsModal={openActionsModal}
            sendMessage={async text => {
              await sendMessageToThread(text);
            }}
          />
        )}

        {!!userVerificationProfile && queryParams.activeTab === Tabs.PROFILE && (
          <TalentCard
            isFooterVisible={false}
            isHideActionVisible={true}
            isVisible={true}
            userId={userVerificationProfile.user?.id}
            wrapperClassName="inbox__user-profile-card"
          />
        )}
      </div>
    </div>
  );
};
